import logging from '@sstdev/lib_logging';
import { http } from 'lib_ui-services';
import { useEffect, useState } from 'react';
import useNetwork from './useNetwork';
import useProfileRole from './useProfileRole';

const _p = {
    http,
    useNetwork,
    useProfileRole,
    debounceMs: 200
};
export const _private = _p;

/**
 * Fetches aggregate (count/groupBy) data from the server via the rules-engine count_ path.
 * Returns null data when offline instead of attempting a local fallback.
 *
 * @param {string} namespace
 * @param {string} relation
 * @param {{
 *   aggregateType?: { aggregateType: 'count' },
 *   groupBy?: { propertyName: string, optionalNamespace?: string, optionalRelation?: string },
 *   groupByBoolean?: { propertyName: string, optionalNamespace?: string, optionalRelation?: string, trueLabel?: string, falseLabel?: string },
 *   includedValues?: { values: string[], propertyName: string, optionalNamespace?: string, optionalRelation?: string, allowUndefined?: boolean },
 *   [filterName: string]: object
 * }} requestFilters - Filter object compatible with getQueryUrl
 * @returns {{ data: object[]|null, isLoading: boolean, isOffline: boolean }}
 */
export default function useServerAggregation(namespace, relation, requestFilters) {
    const { isOnline } = _p.useNetwork();
    const [, roleIsReady] = _p.useProfileRole();
    const [state, setState] = useState({ data: null, isLoading: true, isOffline: false });

    // Serialize filters so useEffect can detect deep changes without referential equality
    const serializedFilters = JSON.stringify(requestFilters);

    useEffect(() => {
        if (!isOnline) {
            // Preserve existing data so charts don't flash to loading if isOnline briefly bounces
            setState(prev => ({ ...prev, isLoading: false, isOffline: true }));
            return;
        }
        if (!roleIsReady) {
            // Online, but the user's role / data-rights are still syncing. Stay in the loading
            // state — NOT the offline indicator (consumers render a cloud-off icon on isOffline) —
            // until the role is ready, so the count reflects data rights. The roleIsReady dep
            // re-runs this effect once the role loads.
            setState(prev => ({ ...prev, isLoading: true, isOffline: false }));
            return;
        }

        let cancelled = false;

        const timer = setTimeout(() => {
            if (cancelled) return;
            setState(prev => ({ ...prev, isLoading: true, isOffline: false }));

            async function fetchData() {
                try {
                    // requestFilters is re-parsed from serializedFilters to avoid stale closure issues
                    const filters = JSON.parse(serializedFilters);
                    const url = _p.http.getQueryUrl(namespace, relation, filters);
                    const result = await _p.http.get(url);
                    if (!cancelled) {
                        // handle both { items: [...] } and raw array shapes from the server
                        const items = result?.items ?? (Array.isArray(result) ? result : []);
                        setState({ data: items, isLoading: false, isOffline: false });
                        cancelled = true;
                    }
                } catch (err) {
                    logging.error('[useServerAggregation] aggregate request failed', err);
                    if (!cancelled) {
                        setState(prev => ({ ...prev, isLoading: false, isOffline: false }));
                    }
                }
            }

            fetchData();
        }, _p.debounceMs);

        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
    }, [namespace, relation, serializedFilters, isOnline, roleIsReady]);

    return state;
}
